import React from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';

const hasActiveDescendant = (item, currentPage) => {
  if (!item.children) {
    return false;
  }

  return item.children.some((child) => {
    if (child.type === 'group') {
      return child.children?.some((leaf) => leaf.id === currentPage);
    }
    return child.id === currentPage;
  });
};

const renderLeaf = ({ leaf, depth, currentPage, onSelect }) => {
  const isActive = currentPage === leaf.id;
  const isDisabled = Boolean(leaf.disabled);
  const IconComponent = leaf.icon;

  return (
    <ListItem
      key={leaf.id}
      component="div"
      disabled={isDisabled}
      sx={{
        pl: depth === 1 ? 2 : 3.5,
        py: 0.25,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        position: 'relative',
        '&::before': {
          display: isActive ? 'block' : 'none',
          content: '""',
          position: 'absolute',
          left: -1,
          top: 0,
          bottom: 0,
          width: '2px',
          bgcolor: 'primary.main',
        },
        '&:hover': {
          bgcolor: 'transparent',
          '& .MuiTypography-root, & .MuiListItemIcon-root': {
            color: isDisabled ? 'text.disabled' : 'primary.main',
          },
        },
      }}
      onClick={() => {
        if (!isDisabled) {
          onSelect(leaf.id);
        }
      }}
      selected={!isDisabled && isActive}
    >
      <ListItemIcon
        sx={{
          minWidth: 28,
          mr: 1,
          color: isDisabled ? 'text.disabled' : isActive ? 'primary.main' : 'text.secondary',
          transition: 'color 0.2s ease-in-out',
        }}
      >
        {IconComponent ? <IconComponent sx={{ fontSize: 16 }} /> : null}
      </ListItemIcon>
      <ListItemText
        primary={leaf.label}
        primaryTypographyProps={{
          fontSize: '0.75rem',
          color: isDisabled ? 'text.disabled' : isActive ? 'primary.main' : 'text.secondary',
          fontWeight: isActive ? 600 : 400,
          transition: 'all 0.2s ease-in-out',
        }}
      />
    </ListItem>
  );
};

const SidebarNav = ({ menuItems, collapsedMenus, onToggleMenu, currentPage, onSelectPage }) => {
  const { canAccess, canAny, canAll } = useAccessControl();

  const isVisible = (item) => {
    if (item.access) {
      const canPassAll = item.access.all ? canAll(item.access.all) : true;
      const canPassAny = item.access.any ? canAny(item.access.any) : true;
      return canPassAll && canPassAny;
    }

    if (Array.isArray(item.permissions) && item.permissions.length > 0) {
      return item.requireAll === false
        ? canAny(item.permissions)
        : canAll(item.permissions);
    }

    return canAccess(item.permission);
  };

  const filterChildren = (children) =>
    children
      .map((child) => {
        if (child.type === 'group') {
          const visibleChildren = (child.children || []).filter(isVisible);
          return visibleChildren.length ? { ...child, children: visibleChildren } : null;
        }
        return isVisible(child) ? child : null;
      })
      .filter(Boolean);

  const isParentVisible = (item) => {
    if (!item.children) return isVisible(item);
    if (item.permission && !isVisible(item)) return false;
    return filterChildren(item.children).length > 0;
  };

  const renderMenuItem = (item) => {
    if (!isParentVisible(item)) return null;
    const IconComponent = item.icon;

    if (item.children) {
      const filteredChildren = filterChildren(item.children);
      if (!filteredChildren.length) return null;
      const isActive = currentPage === item.id || hasActiveDescendant(item, currentPage);
      const isOpen = Boolean(collapsedMenus[item.id]);

      return (
        <React.Fragment key={item.id}>
          <ListItem
            component="div"
            onClick={() => onToggleMenu(item.id)}
            selected={isActive}
            sx={{
              py: 0.5,
              cursor: 'pointer',
              '&:hover': {
                '& .MuiListItemIcon-root': {
                  color: 'primary.main',
                },
                '& .MuiTypography-root': {
                  color: 'primary.main',
                },
              },
              '& .MuiListItemIcon-root': {
                color: isActive ? 'primary.main' : 'inherit',
              },
              '& .MuiTypography-root': {
                color: isActive ? 'primary.main' : 'inherit',
                fontWeight: isActive ? 600 : 500,
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {IconComponent ? (
                <IconComponent
                  sx={{
                    fontSize: 18,
                    transition: 'color 0.2s ease-in-out',
                  }}
                />
              ) : null}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.8rem',
                fontWeight: isActive ? 600 : 500,
                transition: 'color 0.2s ease-in-out',
              }}
            />
            {isOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
          </ListItem>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List
              component="div"
              disablePadding
              sx={{
                position: 'relative',
                ml: 3,
                borderLeft: '1px solid',
                borderColor: 'divider',
              }}
            >
              {filteredChildren.map((child) => {
                if (child.type === 'group') {
                  return (
                    <React.Fragment key={child.id}>
                      <Box sx={{ px: 2, pt: 1, pb: 0.25 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                            color: 'text.disabled',
                          }}
                        >
                          {child.label}
                        </Typography>
                      </Box>
                      {child.children?.map((leaf) =>
                        renderLeaf({
                          leaf,
                          depth: 2,
                          currentPage,
                          onSelect: onSelectPage,
                        }),
                      )}
                    </React.Fragment>
                  );
                }

                return renderLeaf({
                  leaf: child,
                  depth: 1,
                  currentPage,
                  onSelect: onSelectPage,
                });
              })}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    const isActive = currentPage === item.id;
    return (
      <ListItem
        key={item.id}
        component="div"
        onClick={() => onSelectPage(item.id)}
        selected={isActive}
        sx={{
          py: 0.5,
          cursor: 'pointer',
          position: 'relative',
          '&::before': {
            display: isActive ? 'block' : 'none',
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '2px',
            bgcolor: 'primary.main',
          },
          '&:hover': {
            bgcolor: 'transparent',
            '& .MuiListItemIcon-root': {
              color: 'primary.main',
            },
            '& .MuiTypography-root': {
              color: 'primary.main',
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 36,
            color: isActive ? 'primary.main' : 'inherit',
            transition: 'color 0.2s ease-in-out',
          }}
        >
          {IconComponent ? <IconComponent sx={{ fontSize: 18 }} /> : null}
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: '0.8rem',
            fontWeight: isActive ? 600 : 500,
            color: isActive ? 'primary.main' : 'inherit',
            transition: 'color 0.2s ease-in-out',
          }}
        />
      </ListItem>
    );
  };

  return <List>{menuItems.map(renderMenuItem)}</List>;
};

export default SidebarNav;
